import { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/FAQ.css';

const faqs = [
    {
        question: 'What is CensusZen?',
        answer: (
            <p>
                CensusZen reads a home health referral packet and gives the intake team three
                things: an eligibility report that shows whether the patient can start care, a
                pre-visit briefing for the nurse, and diagnosis code suggestions ready for PDGM.
                Every fact links to the page it came from.
            </p>
        ),
    },
    {
        question: 'Who is it for?',
        answer: (
            <p>
                Intake coordinators and clinical liaisons at home health agencies, and the
                administrators who run intake. Nurses read the briefing before the first visit.
                It is not an EMR and does not replace one.
            </p>
        ),
    },
    {
        question: 'What documents do I upload?',
        answer: (
            <p>
                The referral packet as it arrived: hospital discharge summaries, the face-to-face
                encounter note, the physician order or plan of care, medication reconciliations,
                therapy notes. PDF and Word files. Scanned pages are read as images, so a fax
                that came through as a PDF works.
            </p>
        ),
    },
    {
        question: 'How do the face-to-face and homebound checks work?',
        answer: (
            <p>
                CensusZen finds the face-to-face encounter date and the signing date and compares
                them with the planned start of care against the 90-day-before and 30-day-after
                window. It looks for the homebound statement and the skilled need in the
                physician's documentation. A missing or out-of-window face-to-face, a missing
                signed order, and a patient who does not meet the home health criteria are
                flagged as critical. Twenty checks run on every packet; the intake coordinator
                sees each one with its evidence and marks it correct or dismisses it.
            </p>
        ),
    },
    {
        question: 'How are diagnosis codes suggested?',
        answer: (
            <p>
                CensusZen reads the diagnoses in the packet and suggests ICD-10-CM codes from the
                current CMS table, with the sentence in the packet that supports each one. Each
                code is checked against the table for validity and its PDGM grouping, and pairs
                that exclude each other are marked. The coder decides. Suggestions are not a
                coding service and are not billed as one.
            </p>
        ),
    },
    {
        question: 'What does the coordinator still have to do?',
        answer: (
            <p>
                Read the flagged gaps, confirm or correct the extracted facts, and decide whether
                to accept the referral. Nothing is sent, coded, or filed by CensusZen on its own.
                The generated documents are drafts until a person reviews them.
            </p>
        ),
    },
    {
        question: 'Can my team share cases?',
        answer: (
            <p>
                Yes. Cases belong to the agency. Every member of the agency can open a case,
                review facts, and edit review fields. Archiving and deleting stay with the case
                owner and the agency administrator. An administrator can reassign a case when
                someone leaves.
            </p>
        ),
    },
    {
        question: 'Is CensusZen HIPAA compliant?',
        answer: (
            <p>
                CensusZen runs on Google Cloud under Google's business associate agreement, in US
                regions, with encryption at rest and TLS on every connection. Documents are
                processed by Gemini on Vertex AI, which is covered by the same agreement, and
                Google does not use customer data to train its models. Access is scoped to the
                agency, administrators use a second factor, and every action on patient data is
                written to an audit log. A business associate agreement is
                available to every customer. The full control list is in our security overview,
                available on request.
            </p>
        ),
    },
    {
        question: 'What happens to data when a case is archived or deleted?',
        answer: (
            <p>
                Archiving removes the uploaded files and the extracted clinical fields from the
                case and keeps the audit record. Deleting removes the case and its files. Backups
                keep a copy for their retention window, five weeks for the weekly backup, after
                which nothing remains. Cases that sit untouched are archived automatically after
                fourteen days.
            </p>
        ),
    },
    {
        question: 'What is the pricing model?',
        answer: (
            <p>
                We are still working out pricing. Plans are per agency, and an individual tier
                may come later. Until pricing is set, we would rather show you CensusZen on your
                own referral packets.{' '}
                <Link to="/request-access?source=trial_request">Contact us for a free trial</Link>.
            </p>
        ),
    },
    {
        question: 'Can I cancel?',
        answer: (
            <p>
                Yes. Once pricing is set, plans will be month to month with no long-term contract.
                When an agency leaves, its cases are deleted on request.
            </p>
        ),
    },
    {
        question: 'How do I get a demo or a trial?',
        answer: (
            <p>
                <Link to="/request-access?source=demo_request">Book a demo</Link> and bring a
                referral packet. We process it while you watch. Or{' '}
                <Link to="/request-access?source=trial_request">contact us for a free trial</Link>{' '}
                and start with your own packets.
            </p>
        ),
    },
];

export const FAQContent = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <h3>Frequently Asked Questions</h3>
            <p>Common questions about CensusZen, data security, and HIPAA compliance.</p>

            <div className="faq-list">
                {faqs.map((faq, index) => (
                    <div key={index} className={`faq-item ${openIndex === index ? 'faq-item--open' : ''}`}>
                        <button
                            className="faq-question"
                            onClick={() => toggle(index)}
                            aria-expanded={openIndex === index}
                        >
                            <span>{faq.question}</span>
                            <svg
                                className="faq-chevron"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                aria-hidden="true"
                            >
                                <path
                                    d="M4 6L8 10L12 6"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {openIndex === index && (
                            <div className="faq-answer">
                                {faq.answer}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </>
    );
};
